import { useMemo } from 'react';
import Plot from 'react-plotly.js';
import { chartColors } from '../../helpers/chartColors';

export const RTFOFiberStrainChart = ({ currentRTFO, monitorUWI, fiberData, gridView, showSpikeLines, showLegend, darkMode, theme }) => {

  const fiberWellName = fiberData?.[0]?.monitorName || '';

  const data = useMemo(() => {
    if (!fiberData || fiberData?.length === 0) {
      return [];
    };

    return getChartData();

    function getChartData() {
      try {

        const traces = [];
        const wellNames = currentRTFO?.perfSchedule?.wells ? Object.keys(currentRTFO?.perfSchedule?.wells) : [];

        for (const wellName of wellNames) {
          const wellIndex = wellNames.indexOf(wellName);
          const color = chartColors[wellIndex % chartColors.length];

          const X = []; // stage center depth (mMD)
          const Y = []; // strain depth (mMD)
          const Z = []; // strain value

          let wellGroupAdded = false;

          for (const event of fiberData?.filter(e => e.wellName?.toLowerCase() === wellName?.toLowerCase())) {
            const trimmedStageNumber = event.stageNumber;
            const stageClusters = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[trimmedStageNumber]?.clusters;
            const depths = stageClusters && Object.keys(stageClusters)?.map(cluster => stageClusters[cluster]?.shotDepth);    
            const topDepth = depths ? (Math.min(...depths)) : 0;
            const bottomDepth = depths ? (Math.max(...depths)) : 0;
            const stageCenterDepth = (topDepth + bottomDepth) / 2;
            const strainValues = event.strainValues;
            for (let i = 0; i < strainValues.depths?.length; i++) {
              const depth = strainValues.depths[i];
              const positiveValue = strainValues.positiveValues[i];
              const negativeValue = strainValues.negativeValues[i];
              if (depth > 0 && stageCenterDepth > 0) {
                X.push(stageCenterDepth);
                Y.push(depth);
                Z.push(positiveValue);
                X.push(stageCenterDepth);
                Y.push(depth);
                Z.push(negativeValue);
              }
            }

            const maxStrainValue = Math.max(...Z);
            
            const formattedWellName = wellName.charAt(0)?.toUpperCase()?.concat(wellName?.substring(1, ));

            traces.push(
              { // strain data 
                x: X,
                y: Y,
                mode: 'markers',
                type: 'scattergl',
                xaxis: 'x1',
                yaxis: 'y1',
                name: `${formattedWellName} - ${event?.eventId}`,
                legendgroup: formattedWellName,
                showlegend: !wellGroupAdded ? true : false,
                marker: {
                  size: Z.map(val => 4 + val / maxStrainValue * 10),
                  color,
                  fillcolor: color
                },
                line: {
                  color: 'transparent',
                  width: 0
                },
                hoverinfo: !showSpikeLines && 'none',
                connectgaps: true,
              }
            );
            wellGroupAdded = true;
          }
        }
  
        return traces;
  
      } catch (error) {
        console.log(error);
        return [];
      };
    };
  }, [currentRTFO?.perfSchedule?.wells, fiberData, showSpikeLines]);

  const layout = {
    yaxis: { // Event depth y axis
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'black' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      title: {
        text: 'Event Center MD (m)',
        font: {
          size: 12
        },
      },
      autorange: true,
      position: 0,
      anchor: 'x',
      side: 'left',
    },
    xaxis: { // Perf depth x axis
      autorange: true,
      title: {
        text: 'Stage Center MD (m)',
        font: {
          size: 12
        }
      },
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'black' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      anchor: 'y',
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across"
    },
    hovermode: "x",
    hoverdistance: 10, // Distance to show hover label of data point
    spikedistance: 1000,
    autosize: true,
    legend: {
      orientation: "h",
      yanchor: "top",
      font: {
        color: darkMode ? 'lightgrey' : 'grey',
        size: 10
      },
      y: (gridView ? -0.2 : -0.1),
    },
    annotations: [
      {
        text: "Fiber Strain",
          font: {
          size: 14,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.15 : 1.075), //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      {
        text: `Pad: ${currentRTFO?.name} | Fiber Well: ${fiberWellName}`,
          font: {
          size: 10,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.08 : 1.035), //position in y domain
        xref: 'paper',
        yref: 'paper',
      }
    ],
    shapes: [
    ],
    uirevision: true,
    margin: {"t": 60, "l": 60, "r": 60, "b": 60},
    images: [
      {
        x: (gridView ? 0.07 : 0.035),
        y: (gridView ? 1.05 : 1.025),
        sizex: (gridView ? 0.1 : 0.05),
        sizey: 0.1,
        source: process.env.PUBLIC_URL + `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
        xref: "page",
        yref: "y1 domain",
        xanchor: "right",
        yanchor: "bottom",
        opacity: 0.75
      }
    ],
    plot_bgcolor: "transparent",
    paper_bgcolor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]
  };


  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: `${currentRTFO?.name}-Fiber-Strain ${(new Date().toISOString().split('T')[0])}`,
      height: 550,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };


  return (
    <Plot
      data={data}
      layout={layout}
      config={config}
      useResizeHandler={true}
      style={{width: '100%', height: '100%'}}
    />
  );

};
